import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createGeneral, editGeneralById, updateGeneral } from "store/actions/generalActions";
import { getSelectList } from "store/actions/selectListItemAction";
import FormComment from "../../../components/FormComment/FormComment";
import SearchableDropdown from "../../../components/SearchableDropdown/SearchableDropdown";
import {
    TextField,
    Dropdown,
    PrimaryButton,
    DefaultButton,
    Stack,
    DatePicker
} from "@fluentui/react";
import logger from "utils/logger";
import "./AddGeneral.css";
import { getUserId } from "utils/authUtils";

const AddGeneral = ({ closeDrawer, id = null, onRefresh, userRole = null }) => {

    const [originalInformIds, setOriginalInformIds] = useState([]);
    const dispatch = useDispatch();
    const loggedInUserId = getUserId();
    const isResponsible = userRole === 'responsible';

    const editData = useSelector(state => state.general.result);
    const { formLoading: loading, } = useSelector((state) => state.general);
    const { options: selectListItems = [] } = useSelector((state) => state.selectList);

    useEffect(() => {
        if (id) {
            dispatch(editGeneralById(id));
        }
    }, [id, dispatch]);

    useEffect(() => {
        dispatch(getSelectList());
    }, [dispatch])

    // Initialize form data and update when editData changes
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        startDate: null,
        endDate: null,
        responsible: "",
        accountable: [],
        consult: [],
        inform: [],
        priority: "",
        status: "",
        CompletedOn: null,
        remark: "",
    });

    const [commentData, setCommentData] = useState(null);

    const extractIds = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) {
            return data.map(item => {
                if (typeof item === 'object' && item !== null) {
                    return String(item.key || item);
                }
                return String(item);
            });
        }
        return [];
    };

    const extractSingleId = (data) => {
        if (!data) return "";
        if (typeof data === 'object' && data !== null) {
            return String(data.key || "");
        }
        return String(data);
    };

    useEffect(() => {
        setFormData({
            title: "",
            description: "",
            startDate: null,
            endDate: null,
            responsible: "",
            accountable: [],
            consult: [],
            inform: [loggedInUserId],
            priority: "",
            status: "",
            CompletedOn: null,
            remark: "",
        });
        setErrors({});

        if (id) {
            dispatch(editGeneralById(id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, dispatch]);


    useEffect(() => {
        if (id && editData && editData.id === id && selectListItems.length > 0) {

            const validUserIds = selectListItems.map(u => u.key);
            const getValidId = (userId) => {
                if (!userId) return "";
                const id = typeof userId === 'object' ? userId.key : userId;
                return validUserIds.includes(String(id)) ? String(id) : "";
            };
            const getValidIds = (arr) => {
                if (!arr || !Array.isArray(arr)) return [];
                return arr
                    .map(item => typeof item === 'object' ? item.key : item)
                    .filter(id => validUserIds.includes(String(id)))
                    .map(id => String(id));
            };
            const informList = getValidIds(editData.inform);
            setOriginalInformIds(informList); 

            const extractedResponsible = extractSingleId(editData.responsible);
            const validResponsible = getValidId(extractedResponsible);

            setFormData({
                title: editData.title || "",
                description: editData.description || "",
                startDate: editData.startDate ? new Date(editData.startDate) : null,
                endDate: editData.endDate ? new Date(editData.endDate) : null,
                responsible: validResponsible,
                accountable: getValidIds(extractIds(editData.accountable)),
                consult: getValidIds(extractIds(editData.consult)),
                inform: informList,
                priority: editData.priority || "",
                status: editData.status || "",
                CompletedOn: editData.CompletedOn ? new Date(editData.CompletedOn) : null,
                remark: editData.remark || "",
            });
            logger.log("Form data initialized for editing:", formData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, editData, selectListItems]);

    const [errors, setErrors] = useState({});

    const validate = () => {
        const newErrors = {};

        if (!formData.title) newErrors.title = "Title is required.";
        if (!formData.responsible) newErrors.responsible = "Responsible user is required.";
        if (!formData.startDate) newErrors.startDate = "Start Date is required.";
        if (!formData.endDate) newErrors.endDate = "End Date is required.";
        if (!formData.priority) newErrors.priority = "Priority is required.";

        if (formData.startDate && formData.endDate) {
            if (formData.endDate < formData.startDate) {
                newErrors.endDate = "End Date cannot be before Start Date.";
            }
        }

        if (formData.status === 6 && !formData.CompletedOn) {
            newErrors.CompletedOn = "Completion Date is required.";
        }

        if (commentData?.hasUploadingFiles) {
            newErrors.comment = "Please wait for all files to finish uploading.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (name, value) => {
        let updatedValue = value || "";

        if (name === "description") {
            updatedValue = updatedValue.slice(0, 1000);
        }

        if (name === "title") {
            updatedValue = updatedValue.slice(0, 255);
        }

        if (typeof updatedValue === "string") {
            updatedValue = updatedValue.trimStart();
        }

        setFormData((prev) => {
            const updatedFormData = { ...prev, [name]: updatedValue };
            validateField(name, updatedValue, updatedFormData);
            return updatedFormData;
        });
    };

    const validateField = (name, value, formData) => {
        const newErrors = { ...errors };

        switch (name) {
            case "title":
                newErrors.title = value && value.trim() !== "" ? "" : "Title is required.";
                break;
            case "responsible":
                newErrors.responsible = value ? "" : "Responsible user is required.";
                break;
            case "startDate":
                newErrors.startDate = value ? "" : "Start Date is required.";
                if (value && formData.endDate && formData.endDate < value) {
                    newErrors.endDate = "End Date cannot be before Start Date.";
                } else if (!formData.endDate) {
                    newErrors.endDate = errors.endDate;
                } else {
                    newErrors.endDate = "";
                }
                break;
            case "endDate":
                newErrors.endDate = value ? "" : "End Date is required.";
                if (value && formData.startDate && value < formData.startDate) {
                    newErrors.endDate = "End Date cannot be before Start Date.";
                }
                break;
            case "priority":
                newErrors.priority = value ? "" : "Priority is required.";
                break;

            case "CompletedOn":
                if (formData.status === 6) {
                    newErrors.CompletedOn = value ? "" : "Completion Date is required.";
                } else {
                    newErrors.CompletedOn = "";
                }
                break;
            default:
                break;
        }

        setErrors(newErrors);
    };

    const handleBlur = (name, value) => {
        validateField(name, value, formData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            const submissionData = { ...formData };

            if (id) {
                // Add comment object when updating
                if (commentData) {
                    submissionData.comment = {
                        comment: commentData.comment,
                        attachments: commentData.files?.map(f => ({
                            id: f.id || 0,
                            name: f.name,
                            key: f.key,
                        })) || []
                    };
                }
                await dispatch(updateGeneral(`/WorkItems`, { ...submissionData, id }));
            } else {
                await dispatch(createGeneral('/WorkItems', { ...submissionData, status: 1 }));
            }

            if (onRefresh) onRefresh();
            closeDrawer();
        } catch (error) {
            logger.error('Form submission error:', error);
        }
    };

    const getFilteredOptions = (field, options, formData) => {
        return options.filter(opt => {
            switch (field) {
                case "responsible":
                    return (
                        !formData.accountable.includes(opt.key) &&
                        !formData.consult.includes(opt.key) &&
                        !formData.inform.includes(opt.key)
                    );
                case "accountable":
                    return (
                        opt.key !== formData.responsible &&
                        !formData.consult.includes(opt.key) &&
                        !formData.inform.includes(opt.key)
                    );
                case "consult":
                    return (
                        opt.key !== formData.responsible &&
                        !formData.accountable.includes(opt.key) &&
                        !formData.inform.includes(opt.key)
                    );
                case "inform":
                    return (
                        opt.key !== formData.responsible &&
                        !formData.accountable.includes(opt.key) &&
                        !formData.consult.includes(opt.key)
                    );
                default:
                    return true;
            }
        });
    };

    const priority = [
        { key: 1, text: "1-Critical" },
        { key: 2, text: "2-Urgent" },
        { key: 3, text: "3-High" },
        { key: 4, text: "4-Medium" },
        { key: 5, text: "5-Low" }
    ];

    const statusOptions = [
        { key: 1, text: "Pending" },
        { key: 7, text: "In-Progress" },
        { key: 6, text: "Complete" }
    ];

    return (
        <div className="drawer-backdrop">
            <div className={`drawer ${id ? 'task-drawer' : ''}`}>
                <div className="drawer-header">
                    <div className="heading">{id ? "Edit Task" : "Add Task"}</div>
                    <button className="close-btn" onClick={closeDrawer}>&times;</button>
                </div>
                <div className="drawer-content">
                    <form className="form-content" onSubmit={handleSubmit}>
                        <div style={{ display: 'flex', gap: '20px' }}>
                            <div className="w-100">
                                <Stack tokens={{ childrenGap: 16 }}>
                                    <TextField
                                        label={
                                            <div>
                                                Title <span style={{ color: "red" }}>*</span>
                                            </div>
                                        }
                                        type="text"
                                        value={formData.title}
                                        onChange={(e, value) => handleChange("title", value)}
                                        onBlur={() => handleBlur("title", formData.title)}
                                        errorMessage={errors.title}
                                        disabled={isResponsible}
                                    />

                                    <TextField
                                        className="textarea"
                                        label={
                                            <span>
                                                Description{" "}
                                                <span style={{ fontSize: "12px", color: "#666" }}>
                                                    ({formData.description.length}/1000)
                                                </span>
                                            </span>
                                        }
                                        multiline
                                        value={formData.description}
                                        onChange={(e, value) => handleChange("description", value)}
                                        errorMessage={errors.description}
                                        disabled={isResponsible}
                                    />
                                    <div className="row">
                                        <div className="col-6">
                                            <SearchableDropdown
                                                label={
                                                    <div>
                                                        Responsible <span style={{ color: "red" }}>*</span>
                                                    </div>
                                                }
                                                options={getFilteredOptions("responsible", selectListItems, formData)}
                                                selectedKeys={formData.responsible ? [formData.responsible] : []}
                                                onChange={(e, option) => {
                                                    const newValue = option?.key || "";
                                                    handleChange("responsible", newValue);
                                                }}
                                                onBlur={() => handleBlur("responsible", formData.responsible)}
                                                onValueChange={(newValue) => handleBlur("responsible", newValue)}
                                                errorMessage={errors.responsible}
                                                placeholder="Search responsible..."
                                                multiSelect={false}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <SearchableDropdown
                                                label="Accountable"
                                                multiSelect
                                                options={getFilteredOptions("accountable", selectListItems, formData)}
                                                selectedKeys={formData.accountable}
                                                onChange={(e, option) => {
                                                    const updated = option?.selected
                                                        ? [...formData.accountable, option.key]
                                                        : formData.accountable.filter(k => k !== option.key);
                                                    handleChange("accountable", updated);
                                                }}
                                                placeholder="Search accountable..."
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-6">
                                            <SearchableDropdown
                                                label="Consult"
                                                multiSelect
                                                options={getFilteredOptions("consult", selectListItems, formData)}
                                                selectedKeys={formData.consult}
                                                onChange={(e, option) => {
                                                    const updated = option?.selected
                                                        ? [...formData.consult, option.key]
                                                        : formData.consult.filter(k => k !== option.key);
                                                    handleChange("consult", updated);
                                                }}
                                                placeholder="Search consult..."
                                            />
                                        </div>
                                        <div className="col-6">
                                            <SearchableDropdown
                                                label="Inform"
                                                multiSelect
                                                options={getFilteredOptions("inform", selectListItems, formData)}
                                                selectedKeys={formData.inform}
                                                onChange={(e, option) => {
                                                    let updated = [...formData.inform];

                                                    if (option?.selected) {
                                                        updated.push(option.key);
                                                    } else {
                                                        const isAddMode = !id;
                                                        const isCurrentUser = option.key === loggedInUserId;
                                                        const isCreator = option.key === editData?.createdById;
                                                        const loggedInUserWasOriginallyInformed = originalInformIds.includes(loggedInUserId);
                                                        if (
                                                            isCreator ||                                  
                                                            (isCurrentUser && isAddMode) ||               
                                                            (isCurrentUser && loggedInUserWasOriginallyInformed)
                                                        ) {
                                                            return;
                                                        }
                                                        updated = updated.filter(k => k !== option.key);
                                                    }
                                                    handleChange("inform", updated);
                                                }}
                                                placeholder="Search inform..."
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-6">
                                            <DatePicker
                                                className={errors.startDate ? "has-error" : ""}
                                                label={
                                                    <div>
                                                        Start Date <span style={{ color: "#f85149 " }}>*</span>
                                                    </div>
                                                }
                                                value={formData.startDate}
                                                onSelectDate={(date) => handleChange("startDate", date)}
                                                onAfterMenuDismiss={() => {
                                                    setTimeout(() => handleBlur("startDate", formData.startDate), 0);
                                                }}
                                                ariaLabel="Select a start date"
                                                formatDate={(date) =>
                                                    date
                                                        ? `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
                                                            .getDate()
                                                            .toString()
                                                            .padStart(2, "0")}/${date.getFullYear()}`
                                                        : ""
                                                }
                                                disabled={isResponsible}
                                            />
                                            {errors.startDate && (
                                                <div style={{ color: "#f85149", fontSize: "12px", marginTop: "4px" }}>
                                                    {errors.startDate}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-6">
                                            <DatePicker
                                                className={errors.endDate ? "has-error" : ""}
                                                label={
                                                    <div>
                                                        End Date <span style={{ color: "#f85149" }}>*</span>
                                                    </div>
                                                }
                                                value={formData.endDate}
                                                onSelectDate={(date) => handleChange("endDate", date)}
                                                onAfterMenuDismiss={() => {
                                                    setTimeout(() => handleBlur("endDate", formData.endDate), 0);
                                                }}
                                                ariaLabel="Select an end date"
                                                minDate={formData.startDate || undefined}
                                                formatDate={(date) =>
                                                    date
                                                        ? `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
                                                            .getDate()
                                                            .toString()
                                                            .padStart(2, "0")}/${date.getFullYear()}`
                                                        : ""
                                                }
                                                disabled={isResponsible}
                                            />
                                            {errors.endDate && (
                                                <div style={{ color: "#f85149", fontSize: "12px" }}>
                                                    {errors.endDate}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-12">
                                            <Dropdown
                                                label={
                                                    <div>
                                                        Priority <span style={{ color: "red" }}>*</span>
                                                    </div>
                                                }
                                                options={priority}
                                                onBlur={() => handleBlur("priority", formData.priority)}
                                                selectedKey={formData.priority}
                                                onChange={(e, option) => handleChange("priority", option?.key || "")}
                                                errorMessage={errors.priority}
                                                disabled={isResponsible}
                                            />
                                        </div>

                                    </div>
                                </Stack>
                            </div>
                            {id && (
                                <div className="w-100">

                                    <div className="col-12" style={{ marginBottom: '16px' }}>
                                        <Dropdown
                                            label="Status"
                                            options={statusOptions}
                                            selectedKey={formData.status}
                                            onChange={(e, option) => handleChange("status", option?.key || "")}
                                        />
                                    </div>

                                    {id && formData.status === 6 && (
                                        <div className="row" style={{ marginBottom: '16px' }}>
                                            <div className="col-12">
                                                <DatePicker
                                                    label={
                                                        <div>
                                                            Completion Date <span style={{ color: "red" }}>*</span>
                                                        </div>
                                                    }
                                                    value={formData.CompletedOn}
                                                    onSelectDate={(date) => handleChange("CompletedOn", date)}
                                                    onBlur={() => handleBlur("CompletedOn", formData.CompletedOn)}
                                                    ariaLabel="Select a completion date"
                                                    minDate={formData.startDate}
                                                    formatDate={(date) =>
                                                        date
                                                            ? `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
                                                                .getDate()
                                                                .toString()
                                                                .padStart(2, "0")}/${date.getFullYear()}`
                                                            : ""
                                                    }
                                                />
                                                {errors.CompletedOn && (
                                                    <div style={{ color: "#f85149", fontSize: "12px", marginTop: "4px" }}>
                                                        {errors.CompletedOn}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <FormComment onDataChange={setCommentData} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="action-btn">
                            <Stack horizontal tokens={{ childrenGap: 16 }}>
                                <DefaultButton
                                    text="Cancel"
                                    onClick={closeDrawer}
                                    disabled={loading}
                                    style={{ marginBottom: "10px" }}
                                />
                                <PrimaryButton
                                    text={
                                        loading ? id ? "Updating..." : "Submitting..." : id ? "Update" : "Submit"
                                    }
                                    type="submit" style={{ marginBottom: "10px" }}
                                />
                            </Stack>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddGeneral;